package com.timacookies

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.scrollBehavior
import com.varabyte.kobweb.core.App
import com.varabyte.kobweb.core.init.InitKobweb
import com.varabyte.kobweb.core.init.InitKobwebContext
import com.varabyte.kobweb.silk.SilkApp
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.common.SmoothColorStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.loadFromLocalStorage
import com.varabyte.kobweb.silk.theme.colors.saveToLocalStorage
import com.varabyte.kobweb.silk.theme.colors.systemPreference
import kotlinx.browser.window
import org.jetbrains.compose.web.css.vh

private const val COLOR_MODE_KEY = "tima_cookies:colorMode"

@InitKobweb
fun initKobweb(ctx: InitKobwebContext) {
  ctx.router.addRouteInterceptor {
    // Only intercept paths that look like pages
    if (path.contains(".") || path.startsWith("/api/")) return@addRouteInterceptor

    val parts = path.split("/").filter { it.isNotEmpty() }
    val langPrefix = parts.getOrNull(0)?.lowercase()

    if (langPrefix != "en" && langPrefix != "de") {
      val browserLang = window.navigator.language
        .take(2)
        .lowercase()
      val finalLang = if (browserLang == "de") "de" else "en"

      // Rewrite the path internally so Kobweb finds the dynamic {lang} pages
      path = "/$finalLang$path"
    }
  }
}

@InitSilk
fun initColorMode(ctx: InitSilkContext) {
  ctx.config.initialColorMode =
    ColorMode.loadFromLocalStorage(COLOR_MODE_KEY) ?: ColorMode.systemPreference
}

@InitSilk
fun initStyles(ctx: InitSilkContext) {
  ctx.stylesheet.apply {
    registerStyleBase("body") { Modifier.scrollBehavior(ScrollBehavior.Smooth) }
  }
}

@App
@Composable
fun AppEntry(content: @Composable () -> Unit) {
  SilkApp {
    val colorMode = ColorMode.current

    LaunchedEffect(colorMode) {
      colorMode.saveToLocalStorage(COLOR_MODE_KEY)
    }

    // Inject Google Fonts
    LaunchedEffect(Unit) {
      val head = kotlinx.browser.document.head!!
      if (head.querySelector("link[href*='fonts.googleapis.com']") == null) {
        val preconnect1 =
          kotlinx.browser.document.createElement("link") as org.w3c.dom.HTMLLinkElement
        preconnect1.rel = "preconnect"
        preconnect1.href = "https://fonts.googleapis.com"
        head.appendChild(preconnect1)

        val preconnect2 =
          kotlinx.browser.document.createElement("link") as org.w3c.dom.HTMLLinkElement
        preconnect2.rel = "preconnect"
        preconnect2.href = "https://fonts.gstatic.com"
        preconnect2.setAttribute("crossorigin", "")
        head.appendChild(preconnect2)

        val monteSerratLink =
          kotlinx.browser.document.createElement("link") as org.w3c.dom.HTMLLinkElement
        monteSerratLink.rel = "stylesheet"
        monteSerratLink.href =
          "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap"
        head.appendChild(monteSerratLink)

        val notoSerifDisplayLink =
          kotlinx.browser.document.createElement("link") as org.w3c.dom.HTMLLinkElement
        notoSerifDisplayLink.rel = "stylesheet"
        notoSerifDisplayLink.href =
          "https://fonts.googleapis.com/css2?family=Noto+Serif+Display:ital,wght@0,100..900;1,100..900&display=swap"
        head.appendChild(notoSerifDisplayLink)
      }
    }

    Surface(SmoothColorStyle.toModifier().minHeight(100.vh)) {
      content()
    }
  }
}
